import React, { useEffect, useState } from "react";
import Footsteps from "@coninsa-ui/react-footsteps";
import { Toaster } from "@coninsa/core";
import { queryString } from "@coninsa/utils";
import { useQuery } from "@apollo/client";
import { CURRENT_USER_QUERY } from "./graphql/queries";

import Splide from "@coninsa-ui/react-splide";

import {
  defaultFieldSettings,
  getNuwweFieldSettings,
  T_fieldSettings,
} from "./api";

import Auth0 from "./slices/Auth0";
import Auth1 from "./slices/Auth1";

import Step1 from "./slices/Step1";
import Step2 from "./slices/Step2";
import Step3 from "./slices/Step3";
import Step4 from "./slices/Step4";
import Step5 from "./slices/Step5";
import Step6 from "./slices/Step6";

const AUTH_0_STEP = -1;
const AUTH_1_STEP = 0;
const FORM_1_STEP = 1;
const FORM_2_STEP = 2;
const FORM_3_STEP = 3;
const FORM_4_STEP = 4;
const FORM_5_STEP = 5;
const FORM_6_STEP = 6;

export default function AdvertiseProperty() {
  const [step, setStep] = useState(-1);
  const { data, loading } = useQuery(CURRENT_USER_QUERY);
  const [fieldSettings, setFieldSettings] =
    useState<T_fieldSettings>(defaultFieldSettings);

  const handleAuthSuccess = () => {
    setStep(step + 1);
  };

  const handleStepSuccess = (nextStep?: number) => {
    if (nextStep) {
      setStep(nextStep);
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  useEffect(() => {
    const step = queryString.get("step");

    if (step) {
      setStep(Number.parseInt(step));
    }

    async function fetchSettings() {
      const settings = await getNuwweFieldSettings();

      setFieldSettings(settings);
    }

    fetchSettings();
  }, []);

  useEffect(() => {
    queryString.set("step", step);
  }, [step]);

  // Move user to step 1 if it is already logged.
  useEffect(() => {
    if (!loading && data) {
      if (data?.currentUser?.uid !== 0) {
        setStep(1);
      }
    }
  }, [data, loading]);

  const steps = [
    { label: 1, desc: "Déjanos tus datos de contacto" },
    { label: 2, desc: "Ubicación del inmueble" },
    { label: 3, desc: "Información del inmueble" },
    { label: 4, desc: "Fotos del inmueble" },
    { label: 5, desc: "Tarifas de administración y documentación" },
    { label: 6, desc: "Confirmación" },
  ];

  return (
    <div className="bg-zinc-50">
      <header className="headline is-small">
        <h1>
          Publica tu propiedad de vivienda en arriendo sin necesidad de asesoría
        </h1>
      </header>
      {step === FORM_1_STEP && (
        <div className="py-6 md:py-8 container">
          <h2 className="mx-auto mb-6 md:mb-12 md:w-[750px] font-medium text-[var(--color-primary)] md:text-[40px] text-2xl text-center leading-5 md:leading-10">
            <span className="block font-heading text-[34px] text-[var(--color-secondary)] md:text-5xl text-center leading-8 md:leading-10">
              Recuerda tener a la mano
            </span>
            esta información
          </h2>

          <Splide
            options={{
              paddingRight: "60px",
              gap: 16,
              itemsToShow: 3,
            }}
          >
            <div className="co-numeric-card">
              <div className="co-numeric-card__number">1</div>
              <div className="co-numeric-card__content">
                <h4 className="co-heading-4 is-secondary">
                  Información de inmueble
                </h4>
                <p className="text-sm md:text-base">
                  Descripción, ubicación, detalles y características, entre
                  otros.
                </p>
              </div>
            </div>
            <div className="co-numeric-card">
              <div className="co-numeric-card__number">2</div>
              <div className="co-numeric-card__content">
                <h4 className="co-heading-4 is-secondary">
                  Datos del propietario
                </h4>
                <p className="text-sm md:text-base">
                  Los datos básicos del propietario del inmueble.
                </p>
              </div>
            </div>
            <div className="co-numeric-card">
              <div className="co-numeric-card__number">3</div>
              <div className="co-numeric-card__content">
                <h4 className="co-heading-4 is-secondary">Fotografías</h4>
                <p className="text-sm md:text-base">
                  Recuerda tener las fotografías del inmueble y aumentar la
                  visibilidad.
                </p>
              </div>
            </div>
          </Splide>
        </div>
      )}
      <div className="py-16 container">
        {step === AUTH_0_STEP && <Auth0 onSuccess={handleAuthSuccess} />}
        {step === AUTH_1_STEP && <Auth1 onSuccess={handleAuthSuccess} />}

        {step > AUTH_1_STEP && <Footsteps steps={steps} currentStep={step} />}

        {step === FORM_1_STEP && (
          <Step1 onSuccess={handleStepSuccess} fieldSettings={fieldSettings} />
        )}
        {step === FORM_2_STEP && (
          <Step2
            onSuccess={handleStepSuccess}
            onBack={handleBack}
            fieldSettings={fieldSettings}
          />
        )}
        {step === FORM_3_STEP && (
          <Step3
            onSuccess={handleStepSuccess}
            onBack={handleBack}
            fieldSettings={fieldSettings}
          />
        )}
        {step === FORM_4_STEP && (
          <Step4 onSuccess={handleStepSuccess} onBack={handleBack} />
        )}
        {step === FORM_5_STEP && (
          <Step5 onSuccess={handleStepSuccess} onBack={handleBack} />
        )}
        {step === FORM_6_STEP && <Step6 onSuccess={handleStepSuccess} />}
      </div>

      <Toaster position="bottom-right" />
    </div>
  );
}
